import React, { useState } from 'react';
import { X, Plus, Trash2, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { questionnaireService } from '../services/questionnaireService';
import { adminNotificationService } from '../services/adminNotificationService';
import type { Questionnaire } from '../types/questionnaires';

interface AssignQuestionnaireModalProps {
  questionnaire: Questionnaire;
  onClose: () => void;
  onAssigned: () => void;
}

const AssignQuestionnaireModal: React.FC<AssignQuestionnaireModalProps> = ({ 
  questionnaire, 
  onClose, 
  onAssigned 
}) => {
  const { user, userProfile } = useAuth();
  const [emails, setEmails] = useState<string[]>(['']);
  const [deadline, setDeadline] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  const addEmailField = () => {
    setEmails([...emails, '']);
  };

  const removeEmailField = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  const updateEmail = (index: number, value: string) => {
    const updated = [...emails];
    updated[index] = value;
    setEmails(updated);
  };

  const validateEmail = (email: string): boolean => {
    return email.includes('@') && 
           (email.endsWith('@chimperator-live.de') || email.endsWith('@imwizemann.de'));
  };

  const handleAssign = async () => {
    // Validate emails
    const validEmails = emails
      .map(e => e.trim())
      .filter(e => e.length > 0);
    
    if (validEmails.length === 0) {
      alert('Bitte geben Sie mindestens eine E-Mail-Adresse ein');
      return;
    }
    
    const invalidEmails = validEmails.filter(e => !validateEmail(e));
    if (invalidEmails.length > 0) {
      alert(`Ungültige E-Mail-Adressen: ${invalidEmails.join(', ')}\n\nNur @chimperator-live.de und @imwizemann.de sind erlaubt.`);
      return;
    }
    
    if (!deadline) {
      alert('Bitte setzen Sie eine Deadline');
      return;
    }
    
    const deadlineDate = new Date(deadline);
    if (deadlineDate <= new Date()) {
      alert('Die Deadline muss in der Zukunft liegen');
      return;
    }
    
    if (!user || !userProfile) return;
    
    try {
      setIsAssigning(true);
      
      // Create assignments for each email
      const promises = validEmails.map(email => 
        questionnaireService.createAssignment({
          questionnaireId: questionnaire.id,
          questionnaireTitle: questionnaire.title,
          templateId: questionnaire.templateId,
          templateName: questionnaire.templateName,
          assignedTo: email,
          assignedBy: user.uid,
          assignedByName: userProfile.displayName,
          deadline: deadlineDate,
          status: 'assigned'
        })
      );
      
      const assignmentIds = await Promise.all(promises);
      console.log('✅ Assignments created:', assignmentIds);
      
      // Send invitation emails (non-blocking)
      validEmails.forEach((email, index) => {
        adminNotificationService.sendQuestionnaireInvitation({
          recipientEmail: email,
          recipientName: email.split('@')[0],
          questionnaireTitle: questionnaire.title,
          templateName: questionnaire.templateName,
          deadline: deadlineDate,
          assignedByName: userProfile.displayName,
          assignmentId: assignmentIds[index]
        }).catch(err => {
          console.error(`Failed to send invitation to ${email} (non-blocking):`, err);
        });
      });
      
      alert(
        `Fragebogen erfolgreich an ${validEmails.length} Teammitglied${validEmails.length > 1 ? 'er' : ''} zugewiesen!\n\n` +
        `E-Mail-Einladungen werden versendet...`
      );
      onAssigned();
      onClose();
      
    } catch (error) {
      console.error('Error assigning questionnaire:', error);
      alert('Fehler beim Zuweisen. Bitte versuchen Sie es erneut.');
    } finally {
      setIsAssigning(false);
    }
  };

  // Set default deadline to 7 days from now
  useState(() => {
    const defaultDeadline = new Date();
    defaultDeadline.setDate(defaultDeadline.getDate() + 7);
    setDeadline(defaultDeadline.toISOString().slice(0, 16));
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Fragebogen zuweisen</h2>
            <p className="text-sm text-gray-600 mt-1">{questionnaire.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Email addresses */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Teammitglieder <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-600 mb-4">
              E-Mail-Adressen von @chimperator-live.de oder @imwizemann.de
            </p>
            
            <div className="space-y-3">
              {emails.map((email, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => updateEmail(index, e.target.value)}
                    placeholder="name@chimperator-live.de"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  {emails.length > 1 && (
                    <button
                      onClick={() => removeEmailField(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            <button
              onClick={addEmailField}
              className="mt-3 text-purple-600 hover:text-purple-700 flex items-center text-sm font-medium"
            >
              <Plus className="w-4 h-4 mr-1" />
              Weitere Person hinzufügen
            </button>
          </div>

          {/* Deadline */}
          <div>
            <label htmlFor="deadline" className="block text-sm font-semibold text-gray-900 mb-2">
              Deadline <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Nach dieser Deadline können Teammitglieder den Fragebogen nicht mehr bearbeiten.
            </p>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="datetime-local"
                id="deadline"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Info box */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 mb-2">ℹ️ Was passiert nach der Zuweisung?</h4>
            <ul className="text-sm text-purple-800 space-y-1">
              <li>• Teammitglieder sehen den Fragebogen beim nächsten Login</li>
              <li>• Sie werden automatisch zum Ausfüllen weitergeleitet</li>
              <li>• Antworten werden alle 10 Sekunden automatisch gespeichert</li>
              <li>• Nach der Deadline wird der Fragebogen schreibgeschützt</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleAssign}
              disabled={isAssigning}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAssigning ? 'Weise zu...' : 'Jetzt zuweisen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignQuestionnaireModal;

